import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import logo from '../../logo.svg';
import { AppContext } from '../../hooks/AppProvider';
import { useLogout } from '../../hooks/useAuth';
import { getPath } from '../../utils/navigation';
import { SearchBar } from './SearchBar';
import { MobileMenu } from './MobileMenu';
import { UserMenu } from './UserMenu';
// import { ThemeToggle } from './ThemeToggle';
import { motion, useScroll } from 'framer-motion';

type NavItem = {
  name: string;
  href: string;
};

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const { user, setUser } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();

  // Use the React Query logout hook
  const logoutMutation = useLogout({
    navigate,
    setUser,
  });

  const isHomePage = location.pathname === '/';

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path === '/usage') {
      return 'Usage';
    }
    if (path === '/pricing') {
      return 'Pricing';
    }
    return '';
  });

  const desktopSearchRef = useRef<HTMLInputElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  const { scrollY } = useScroll();
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    return scrollY.onChange((latest) => {
      setHasScrolled(latest > 0);
    });
  }, [scrollY]);

  // Update active tab when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path === '/usage') {
      setActiveTab('Usage');
    } else if (path === '/pricing') {
      setActiveTab('Pricing');
    } else if (path === '/') {
      setActiveTab('');
    }
  }, [location.pathname]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        (!document.activeElement ||
          !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName))
      ) {
        e.preventDefault();
        if (isHomePage) {
          return;
        }
        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop && desktopSearchRef.current) {
          desktopSearchRef.current.focus();
        } else if (mobileSearchRef.current) {
          mobileSearchRef.current.focus();
        }
      }

      if (e.key === 'Escape') {
        if (desktopSearchRef.current === document.activeElement) {
          e.preventDefault();
          desktopSearchRef.current?.blur();
        }
        if (mobileSearchRef.current === document.activeElement) {
          e.preventDefault();
          mobileSearchRef.current?.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isHomePage]);

  const onLogoutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    logoutMutation.mutate();
  };

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const navItems: NavItem[] = !user
    ? [
        { name: 'Who We Serve', href: 'https://www.elementus.io/who-we-serve' },
        { name: 'About', href: 'https://www.elementus.io/about-us' },
        { name: 'Blog', href: 'https://www.elementus.io/blog' },
        { name: 'Press', href: 'https://www.elementus.io/press' },
      ]
    : [];

  const handleSearch = useCallback(() => {
    if (searchInput.trim().length === 0) {
      return;
    }
    navigate(getPath(searchInput));
    setSearchInput('');
  }, [searchInput, navigate]);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [],
  );

  return (
    <motion.header
      className="sticky top-0 z-50 h-20 bg-background/85 backdrop-blur-md"
      role="header"
      animate={{
        boxShadow: hasScrolled
          ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)'
          : 'none',
      }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex h-20 items-center px-4 md:px-6">
        {/* Logo */}
        <div className="mb-1.5 ml-6 mr-6">
          <Link to="/" onClick={() => setActiveTab('')} aria-label="Home">
            <img
              src={logo}
              alt="Elementus Logo"
              className="h-10 w-auto min-w-[120px] object-contain"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:flex-1" aria-label="Main navigation">
          <ul className="flex space-x-4">
            {navItems.map((item) => (
              <li key={item.name}>
                {item.href.startsWith('http') ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'relative flex h-16 items-center whitespace-nowrap px-4 text-sm font-medium transition-colors hover:text-primary',
                      'text-muted-foreground',
                    )}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'relative flex h-16 items-center whitespace-nowrap px-4 text-sm font-medium transition-colors hover:text-primary',
                      activeTab === item.name
                        ? 'text-primary after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-primary'
                        : 'text-muted-foreground',
                    )}
                    onClick={() => setActiveTab(item.name)}
                    aria-current={activeTab === item.name ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Search and Actions */}
        <div className="ml-auto flex w-full items-center gap-4 lg:w-auto">
          {/* Desktop Search */}
          <div className="relative hidden min-w-[350px] max-w-[600px] flex-1 lg:block">
            <SearchBar
              innerRef={desktopSearchRef}
              isFocused={isSearchFocused}
              setIsFocused={setIsSearchFocused}
              value={searchInput}
              onChange={handleSearchInputChange}
              onSearch={handleSearch}
            />
          </div>

          {/* Mobile Search */}
          <div className="relative flex-1 px-2 lg:hidden">
            <SearchBar
              innerRef={mobileSearchRef}
              isFocused={isSearchFocused}
              setIsFocused={setIsSearchFocused}
              value={searchInput}
              onChange={handleSearchInputChange}
              onSearch={handleSearch}
            />
          </div>

          {/* Theme Toggle */}
          {/* <ThemeToggle /> */}

          {/* User Menu */}
          <div className="mr-6 hidden lg:flex">
            <UserMenu user={user} onLogoutClick={onLogoutClick} />
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 lg:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        navItems={navItems}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        onLogoutClick={onLogoutClick}
        onClose={closeMobileMenu}
      />
    </motion.header>
  );
};
